export const StreamProcessorSrc = URL.createObjectURL(new Blob([`
  class StreamProcessor extends AudioWorkletProcessor {
    constructor() {
      super();
      this.buffers = {};
      this.trackIds = {};
      this.currentTrackId = null;
      this.volume = 1.0;
      this.sampleOffset = 0;
      // Playback jitter buffer target in milliseconds (configurable via message)
      this.minBufferMs = 400; // default buffer (will be adjusted by network monitor)
      this.minBufferSamples = 0; // computed from minBufferMs & sampleRate
      this.graceFrames = 0;
      this.maxGraceFrames = 50; // Increased grace frames for smoother playback
      this.completedTracks = new Set();
      this.totalSamplesProcessed = 0;
      this.sampleRate = 44100;
      // Maintain a FIFO queue of trackIds in order of arrival
      this.playQueue = [];
      // Track if we're in low-buffer state to prevent stuttering
      this.isBuffering = false;
      // Track last sample value for smooth transitions
      this.lastSample = 0;

      this.port.onmessage = (e) => {
        const { event } = e.data;
        if (event === 'write') {
          const { buffer, trackId } = e.data;
          
          // If this trackId was previously completed, reset it for new audio
          if (this.completedTracks.has(trackId)) {
            this.completedTracks.delete(trackId);
            delete this.buffers[trackId];
            delete this.trackIds[trackId];
          }
          
          if (!this.buffers[trackId]) {
            this.buffers[trackId] = new Float32Array(0);
            this.trackIds[trackId] = trackId;
            // New track: enqueue if not already present
            if (!this.playQueue.includes(trackId)) {
              this.playQueue.push(trackId);
            }
          }

          const floatBuffer = new Float32Array(buffer.length);
          for (let i = 0; i < buffer.length; i++) {
            floatBuffer[i] = buffer[i] / 32768.0;
          }

          const newBuffer = new Float32Array(this.buffers[trackId].length + floatBuffer.length);
          newBuffer.set(this.buffers[trackId]);
          newBuffer.set(floatBuffer, this.buffers[trackId].length);
          this.buffers[trackId] = newBuffer;
          // Only start playing when idle AND there is enough buffered audio; do NOT preempt current track
          if (this.currentTrackId === null) {
            // Dequeue next available track with sufficient buffered audio
            while (this.playQueue.length > 0) {
              const nextId = this.playQueue[0];
              const buf = this.buffers[nextId];
              const minSamples = this._getMinBufferSamples();
              if (buf && buf.length >= minSamples && !this.completedTracks.has(nextId)) {
                this.currentTrackId = nextId;
                this.sampleOffset = 0;
                this.totalSamplesProcessed = 0;
                this.graceFrames = 0;
                break;
              } else if (!buf || this.completedTracks.has(nextId)) {
                // Remove empty or completed entries
                this.playQueue.shift();
              } else {
                // Not enough buffered yet; wait for more data
                break;
              }
            }
          } else if (this.currentTrackId === trackId) {
            // Keep current track alive
            this.graceFrames = 0;
          }

        } else if (event === 'offset' || event === 'interrupt') {
          const { requestId } = e.data;
          this.port.postMessage({
            event: 'offset',
            requestId,
            trackId: this.currentTrackId,
            offset: this.sampleOffset
          });

          if (event === 'interrupt' && this.currentTrackId) {
            delete this.buffers[this.currentTrackId];
            delete this.trackIds[this.currentTrackId];
            this.completedTracks.delete(this.currentTrackId);
            this.currentTrackId = null;
            this.sampleOffset = 0;
            this.totalSamplesProcessed = 0;
            this.graceFrames = 0;
            // Advance to next queued track, if any
            while (this.playQueue.length > 0) {
              const nextId = this.playQueue.shift();
              const buf = this.buffers[nextId];
              const minSamples = this._getMinBufferSamples();
              if (buf && buf.length >= minSamples && !this.completedTracks.has(nextId)) {
                this.currentTrackId = nextId;
                this.totalSamplesProcessed = 0;
                break;
              }
            }
          }

        } else if (event === 'volume') {
          this.volume = e.data.volume;
        } else if (event === 'buffer.ms') {
          const { value } = e.data;
          const clamped = Math.max(0, Math.min(2000, Number(value) || 0));
          this.minBufferMs = clamped;
        } else if (event === 'track.complete') {
          const { trackId } = e.data;
          this.completedTracks.add(trackId);
        } else if (event === 'track.activation.complete') {
          const { trackId } = e.data;
          this.completedTracks.add(trackId);
        }
      };
    }

    process(inputs, outputs) {
      const output = outputs[0];
      const channel = output[0];
      this.sampleRate = sampleRate;
      // Update target min buffer samples each render quantum
      this.minBufferSamples = this._getMinBufferSamples();

      if (this.currentTrackId && this.buffers[this.currentTrackId]) {
        const buffer = this.buffers[this.currentTrackId];
        const remainingSamples = buffer.length - this.sampleOffset;
        const isTrackComplete = this.completedTracks.has(this.currentTrackId);

        if (remainingSamples > 0) {
          // Process as many samples as available, up to channel.length
          const samplesToProcess = Math.min(remainingSamples, channel.length);
          
          // Apply fade-in if resuming from buffering (prevents clicks when audio resumes)
          const wasBuffering = this.isBuffering;
          if (wasBuffering && samplesToProcess > 0) {
            const fadeInLength = Math.min(64, samplesToProcess); // Increased to 64 samples for smoother transitions
            for (let i = 0; i < fadeInLength; i++) {
              // Use cosine curve for smoother fade (more natural than linear)
              const fadeGain = 0.5 * (1 - Math.cos(Math.PI * i / fadeInLength));
              channel[i] = buffer[this.sampleOffset + i] * this.volume * fadeGain;
            }
            // Process remaining samples normally
            for (let i = fadeInLength; i < samplesToProcess; i++) {
              channel[i] = buffer[this.sampleOffset + i] * this.volume;
            }
          } else {
            // Normal processing
            for (let i = 0; i < samplesToProcess; i++) {
              channel[i] = buffer[this.sampleOffset + i] * this.volume;
            }
          }
          
          // Apply fade-out to prevent clicks when chunk ends mid-frame
          if (samplesToProcess < channel.length) {
            const fadeLength = Math.min(64, samplesToProcess); // Increased to 64 samples (~1.3ms at 48kHz) for smoother transitions
            for (let i = 0; i < fadeLength; i++) {
              const fadeIdx = samplesToProcess - fadeLength + i;
              if (fadeIdx >= 0) {
                // Use cosine curve for smoother fade (more natural than linear)
                const fadeGain = 0.5 * (1 + Math.cos(Math.PI * i / fadeLength));
                channel[fadeIdx] *= fadeGain;
              }
            }
            // Fill the rest with silence
            for (let i = samplesToProcess; i < channel.length; i++) {
              channel[i] = 0;
            }
          }
          
          // Track last sample for smooth transitions
          if (samplesToProcess > 0) {
            this.lastSample = channel[samplesToProcess - 1];
          }
          
          this.sampleOffset += samplesToProcess;
          this.totalSamplesProcessed += samplesToProcess;
          this.graceFrames = 0;
          this.isBuffering = false;

        } else if (this.sampleOffset >= buffer.length && !isTrackComplete) {
          // Track is still streaming but we've consumed all buffered samples
          // Wait (output silence) to allow more data to arrive - this prevents stuttering
          this.graceFrames++;
          
          // Log when we first enter buffering state
          if (this.graceFrames === 1) {
            this.port.postMessage({
              event: 'buffering_start',
              trackId: this.currentTrackId,
              bufferLength: buffer.length,
              sampleOffset: this.sampleOffset
            });
          }
          
          for (let i = 0; i < channel.length; i++) {
            channel[i] = 0;
          }
          this.isBuffering = true;
          
          // If we've been waiting too long, log a warning
          if (this.graceFrames === this.maxGraceFrames) {
            this.port.postMessage({
              event: 'buffer_underrun',
              trackId: this.currentTrackId,
              graceFrames: this.graceFrames
            });
          }
          return true;

        } else if (this.sampleOffset >= buffer.length && isTrackComplete) {
          const bufferDurationSeconds = buffer.length / this.sampleRate;
          const processedDurationSeconds = this.totalSamplesProcessed / this.sampleRate;

          if (this.graceFrames < this.maxGraceFrames) {
            this.graceFrames++;
            for (let i = 0; i < channel.length; i++) {
              channel[i] = 0;
            }
            return true;
          }

          if (processedDurationSeconds >= bufferDurationSeconds) {
            this.port.postMessage({ 
              event: 'stop', 
              trackId: this.currentTrackId,
              bufferLength: buffer.length,
              finalOffset: this.sampleOffset
            });

            // Move to next queued track (FIFO)
            this.currentTrackId = null;
            this.sampleOffset = 0;
            this.totalSamplesProcessed = 0;
            this.graceFrames = 0;
            while (this.playQueue.length > 0) {
              const nextId = this.playQueue.shift();
              const buf = this.buffers[nextId];
              if (buf && buf.length >= this.minBufferSamples && !this.completedTracks.has(nextId)) {
                this.currentTrackId = nextId;
                this.totalSamplesProcessed = 0;
                break;
              }
            }

            for (let i = 0; i < channel.length; i++) {
              channel[i] = 0;
            }
          } else {
            for (let i = 0; i < channel.length; i++) {
              channel[i] = 0;
            }
          }
        } else {
          // Output silence if track not completed or no samples to process
          for (let i = 0; i < channel.length; i++) {
            channel[i] = 0;
          }
        }
      } else {
        // No track, output silence
        for (let i = 0; i < channel.length; i++) {
          channel[i] = 0;
        }
      }

      return true;
    }

    _getMinBufferSamples() {
      const sr = this.sampleRate || 44100;
      const ms = this.minBufferMs || 0;
      const samples = Math.max(0, Math.floor((sr * ms) / 1000));
      return samples;
    }
  }

  registerProcessor('stream_processor', StreamProcessor);
`], { type: 'application/javascript' }));